/**
 * LineVoiceOfPicker.jsx
 *
 * Whose voice comes out of this line, when it is not the speaker's own.
 *
 * ⛔ THIS IS NOT A REASSIGNMENT, AND THE DIFFERENCE IS THE WHOLE POINT. Reassigning says somebody
 * ELSE said the line: the cast grid moves it, the counts move with it, the credit moves with it.
 * This says the right person said it and it came out sounding like somebody else — mimicry, an
 * impersonation, possession, a recording, a character putting on an accent, a machine cloning whoever
 * it has heard. The speaker keeps the line.
 *
 * ⭐ WHY THE APP NEEDED IT. Voice selection is keyed on the CHARACTER, so one character has exactly
 * one voice per emotion, and a morphing character could only be recorded by lying: a separate
 * character per borrowed voice ("Mister Diogenes QPack"), or by giving the line away — which is how
 * Jasper's line count came to include a line Glendyl says in his voice (Luck ch72). See
 * docs/journal/LUCK_VOICE_COMPLETION_AND_PROMPTS.md §12 and server voiceImpressionStore.js.
 *
 * ⭐ WHY IT IS A ROW CONTROL, like the kind picker beside it: you find out that a line is an
 * impression by READING it, in context. The narration one line up is what tells you — *"The QPack had
 * shifted voices and was now doing its too-lifelike imitation of Diogenes"* — and a fix that needs
 * you to go somewhere else is a fix that mostly does not happen.
 */

import React from "react";
import { Dropdown, Popup } from "semantic-ui-react";

const CLEAR = "__clear__";

/**
 * The roster, as a menu.
 *
 * ⚠️ VALUES ARE UUIDS, never names. The server stores a uuid for the reason `kindVoices` and
 * `sectionVoices` do — a rename or a merge cannot leave a stale string behind — and a picker that
 * sent a name would work until the first merge.
 */
const optionsFor = (characters) => ([
  { key: CLEAR, value: CLEAR, text: "— own voice —" },
  ...(characters || [])
    .filter((c) => c && c.uuid && c.name)
    .map((c) => ({
      key: c.uuid,
      value: c.uuid,
      text: c.name,
      // Whether that character can actually lend a voice. Borrowing from somebody uncast changes
      // nothing audible, and the resolver returns the line to its own speaker — so say so here,
      // at the moment of choosing, rather than leaving it to be discovered by ear.
      description: c.hasVoice === false ? "no voice" : undefined,
    })),
]);

/**
 * The inline control on a line-set row. Text-free when nothing is set, so a screen of ordinary
 * dialogue stays readable and the borrowed ones stand out.
 */
export const LineVoiceOfCell = ({ row, characters, onSetVoiceOf, busy }) => {
  if (!characters || !characters.length || !onSetVoiceOf) return null;
  // ⚠️ `row.spokenInVoiceOf` is the NAME resolved at boot, so it already accounts for a target that
  // has since been merged or dismissed; the dropdown's value is the uuid, which may no longer match
  // any option. Both are read, and neither is derived from the other.
  const currentName = row.spokenInVoiceOf || null;
  const entry = currentName
    ? (characters.find((c) => c && c.name === currentName) || null)
    : null;

  return (
    <Dropdown
      className="rud-voiceof-picker"
      inline
      search
      disabled={!!busy}
      options={optionsFor(characters)}
      value={entry ? entry.uuid : CLEAR}
      selectOnBlur={false}
      onChange={(_, d) => onSetVoiceOf([row.lineId], d.value === CLEAR ? null : d.value)}
      trigger={
        currentName ? (
          <Popup
            size="mini"
            wide
            content={`Spoken by ${row.speaker || "(no speaker)"}, in ${currentName}'s voice. `
              + `Not a reassignment — the line and its count stay with ${row.speaker || "the speaker"}.`}
            // ⭐ Trigger spans the cell so the tooltip follows the pointer anywhere in it; the chip
            // keeps its size. See the click-zone note in RenderingStudio.css.
            trigger={
              <span className="rud-cell-trigger">
                <span className="rud-voiceof-chip">▸ {currentName}</span>
              </span>
            }
          />
        ) : (
          <Popup
            size="mini"
            wide
            content="Borrow another character's voice for this line — an impersonation, a recording, a mimic. The speaker keeps the line."
            trigger={
              <span className="rud-cell-trigger">
                <span className="rud-voiceof-chip rud-voiceof-chip-empty">—</span>
              </span>
            }
          />
        )
      }
    />
  );
};

/**
 * The same choice for every ticked line at once.
 *
 * ⭐ THE REASON THE ENDPOINT TAKES AN ARRAY, and it is not a convenience here: an impression is
 * almost never one line. Luck's QPack holds the Diogenes voice across a whole scene — four lines in
 * ch15–16, and the narration announces the shift once at the start and the switch back once at the
 * end. Marking a run one line at a time is how half a scene ends up in the wrong voice.
 */
export const LineVoiceOfBulkPicker = ({ characters, count, onSetVoiceOf, busy }) => {
  if (!characters || !characters.length || !onSetVoiceOf || !count) return null;
  return (
    <Dropdown
      button
      basic
      compact
      className="icon rud-voiceof-bulk"
      icon="exchange"
      floating
      labeled
      search
      disabled={!!busy}
      loading={!!busy}
      text={`Voice of (${count})`}
      options={optionsFor(characters)}
      value={null}
      selectOnBlur={false}
      onChange={(_, d) => onSetVoiceOf(null, d.value === CLEAR ? null : d.value)}
    />
  );
};

export default LineVoiceOfCell;
